// Overview tab — the at-a-glance quarterly picture: headline stats, the
// judged-signal breakdown (what the AI classifier concluded the changes
// actually mean), the signal mix, and the companies gaining / losing the
// most talent in the period.

import React, { useMemo } from 'react';
import {
  MonitoringReportData,
  SIGNAL_TYPE_LABELS,
  AI_CATEGORY_LABELS,
  formatPeriodRange,
} from './reportData';

interface MonitoringOverviewProps {
  data: MonitoringReportData | null;
  loading: boolean;
  onGoToMovements: () => void;
  onGoToSignals: () => void;
}

// Trend badge vs the previous period of the same length. Neutral ink — more
// signals isn't "good" or "bad", it's activity.
const TrendDelta: React.FC<{ current: number; previous: number | undefined | null }> = ({ current, previous }) => {
  if (previous == null) return null;
  if (previous === 0 && current === 0) return null;
  let text: string;
  if (previous === 0) text = 'new activity';
  else {
    const pct = Math.round(((current - previous) / previous) * 100);
    text = pct === 0 ? 'level' : `${pct > 0 ? '▲' : '▼'} ${Math.abs(pct)}%`;
  }
  return (
    <span className="inline-block mt-1 text-[11px] font-medium text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded-full">
      {text} <span className="text-violet-400 font-normal">vs prev. period</span>
    </span>
  );
};

// Movements-per-bucket sparkline: single series, brand violet, 2px line.
// Buckets are weeks, or months when the period is long.
const MovementsSparkline: React.FC<{ data: MonitoringReportData }> = ({ data }) => {
  const from = new Date(data.period.from).getTime();
  const to = new Date(data.period.to).getTime();
  const spanDays = Math.max(1, (to - from) / 86400000);
  const bucketMs = (spanDays > 120 ? 30 : 7) * 86400000;
  const nBuckets = Math.max(2, Math.min(26, Math.ceil(spanDays * 86400000 / bucketMs)));
  const counts = new Array(nBuckets).fill(0);
  for (const m of data.movements) {
    const ts = new Date(m.date).getTime();
    if (!isFinite(ts)) continue;
    const i = Math.min(nBuckets - 1, Math.max(0, Math.floor((ts - from) / bucketMs)));
    counts[i]++;
  }
  const max = counts.reduce((a, b) => Math.max(a, b), 0);
  if (max === 0) return null;
  const W = 120, H = 30, PAD = 3;
  const x = (i: number) => PAD + (i / (nBuckets - 1)) * (W - 2 * PAD);
  const y = (v: number) => H - PAD - (v / max) * (H - 2 * PAD);
  const pts = counts.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const area = `${PAD},${H - PAD} ${pts} ${(W - PAD).toFixed(1)},${H - PAD}`;
  const unit = spanDays > 120 ? 'month' : 'week';
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-28 h-7 mt-1" role="img" aria-label={`Movements per ${unit}`}>
      <title>{`Movements per ${unit}: ${counts.join(', ')}`}</title>
      <polygon points={area} fill="#7c3aed" opacity={0.12} />
      <polyline points={pts} fill="none" stroke="#7c3aed" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={x(nBuckets - 1)} cy={y(counts[nBuckets - 1])} r={2.5} fill="#7c3aed" />
    </svg>
  );
};

const MonitoringOverview: React.FC<MonitoringOverviewProps> = ({
  data, loading, onGoToMovements, onGoToSignals,
}) => {
  const aiCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const s of data?.signals || []) {
      if (s.ai_category) counts[s.ai_category] = (counts[s.ai_category] || 0) + 1;
    }
    return counts;
  }, [data]);

  const typeEntries = useMemo(() => {
    const byType = data?.summary.byType || {};
    return Object.keys(byType)
      .map(t => ({ type: t, label: SIGNAL_TYPE_LABELS[t] || t, count: byType[t] }))
      .sort((a, b) => b.count - a.count);
  }, [data]);
  const maxType = typeEntries.length > 0 ? typeEntries[0].count : 0;

  if (loading && !data) {
    return <div className="card p-6"><p className="text-gray-600 animate-fade-in">Loading market overview…</p></div>;
  }
  if (!data) {
    return <div className="card p-6"><p className="text-gray-600">Could not load the market overview. Try another period.</p></div>;
  }

  const { summary, arrivals, departures } = data;
  const judgedTotal = Object.keys(aiCounts).reduce((a, k) => a + aiCounts[k], 0);

  const aiTiles = [
    { key: 'genuine_promotion', icon: '🏆', tone: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
    { key: 'new_role_same_employer', icon: '🔄', tone: 'text-blue-700 bg-blue-50 border-blue-200' },
    { key: 'open_to_work_signal', icon: '🟢', tone: 'text-teal-700 bg-teal-50 border-teal-200' },
    { key: 'side_venture_added', icon: '🚀', tone: 'text-amber-700 bg-amber-50 border-amber-200' },
    { key: 'profile_refresh_only', icon: '✍️', tone: 'text-gray-600 bg-gray-50 border-gray-200' },
  ].filter(t => (aiCounts[t.key] || 0) > 0);

  return (
    <div className="space-y-4">
      {/* Headline stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <button onClick={onGoToSignals} className="stat-card border-l-4 border-violet-500 text-left transition-all duration-200 hover:shadow-card-hover hover:-translate-y-px">
          <p className="text-xs text-gray-600 mb-1">Signals detected</p>
          <p className="text-3xl font-bold tracking-tight text-gray-900">{summary.total}</p>
          <p className="text-xs text-gray-500 mt-1">{formatPeriodRange(data.period)}</p>
          <TrendDelta current={summary.total} previous={data.previous?.total} />
        </button>
        <button onClick={onGoToMovements} className="stat-card border-l-4 border-blue-500 text-left transition-all duration-200 hover:shadow-card-hover hover:-translate-y-px">
          <p className="text-xs text-gray-600 mb-1">Market moves</p>
          <p className="text-3xl font-bold tracking-tight text-blue-600">{summary.totalMovements}</p>
          <p className="text-xs text-gray-500 mt-1">Candidates who changed company</p>
          <TrendDelta current={summary.totalMovements} previous={data.previous?.totalMovements} />
          <MovementsSparkline data={data} />
        </button>
        <button onClick={onGoToMovements} className="stat-card border-l-4 border-green-500 text-left transition-all duration-200 hover:shadow-card-hover hover:-translate-y-px">
          <p className="text-xs text-gray-600 mb-1">Companies gaining talent</p>
          <p className="text-3xl font-bold tracking-tight text-green-600">{summary.companiesGaining}</p>
          <p className="text-xs text-gray-500 mt-1">Hired from your mapped market</p>
          <TrendDelta current={summary.companiesGaining} previous={data.previous?.companiesGaining} />
        </button>
        <button onClick={onGoToMovements} className="stat-card border-l-4 border-red-500 text-left transition-all duration-200 hover:shadow-card-hover hover:-translate-y-px">
          <p className="text-xs text-gray-600 mb-1">Companies losing talent</p>
          <p className="text-3xl font-bold tracking-tight text-red-600">{summary.companiesLosing}</p>
          <p className="text-xs text-gray-500 mt-1">Departures &amp; backfill openings</p>
          <TrendDelta current={summary.companiesLosing} previous={data.previous?.companiesLosing} />
        </button>
      </div>

      {/* Judged-signal strip — what the changes actually MEAN */}
      {judgedTotal > 0 && (
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold tracking-tight text-gray-900">🧠 What the changes actually mean</h3>
            <span className="text-xs text-gray-500">{judgedTotal} signal{judgedTotal === 1 ? '' : 's'} judged by AI</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {aiTiles.map(t => (
              <div key={t.key} className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${t.tone}`}>
                <span>{t.icon}</span>
                <span className="text-sm font-semibold">{aiCounts[t.key]}</span>
                <span className="text-xs">{AI_CATEGORY_LABELS[t.key] || t.key}</span>
              </div>
            ))}
          </div>
          {(aiCounts['profile_refresh_only'] || 0) > 0 && (
            <p className="text-xs text-gray-500 mt-2.5">
              Profile refreshes are cosmetic edits (headline wording, skills, summaries) — judged and
              kept out of your promotion signals so the feed stays truthful.
            </p>
          )}
        </div>
      )}

      {/* Signal mix + top movers */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Signal mix */}
        <div className="card p-5">
          <h3 className="text-base font-semibold tracking-tight text-gray-900 mb-3">Signal mix</h3>
          {typeEntries.length === 0 ? (
            <p className="text-sm text-gray-500">No signals in this period.</p>
          ) : (
            <div className="space-y-2.5">
              {typeEntries.map(t => (
                <div key={t.type}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-700 font-medium">{t.label}</span>
                    <span className="text-gray-500">{t.count}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-violet-500 to-violet-700"
                      style={{ width: `${maxType > 0 ? Math.max(6, (t.count / maxType) * 100) : 0}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top destinations */}
        <div className="card p-5">
          <h3 className="text-base font-semibold tracking-tight text-gray-900 mb-3">📈 Where talent is going</h3>
          {arrivals.length === 0 ? (
            <p className="text-sm text-gray-500">No arrivals detected in this period.</p>
          ) : (
            <ul className="space-y-2">
              {arrivals.slice(0, 6).map(a => (
                <li key={a.company} className="flex items-center justify-between gap-2">
                  <button onClick={onGoToMovements} className="min-w-0 text-left group">
                    <span className="text-sm font-medium text-gray-800 group-hover:text-violet-700 group-hover:underline truncate block" title={a.company}>{a.company}</span>
                    {a.industry && <span className="text-xs text-gray-500 truncate block">{a.industry}</span>}
                  </button>
                  <span className="text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full whitespace-nowrap">+{a.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Top losses */}
        <div className="card p-5">
          <h3 className="text-base font-semibold tracking-tight text-gray-900 mb-3">📉 Who is losing talent</h3>
          {departures.length === 0 ? (
            <p className="text-sm text-gray-500">No departures detected in this period.</p>
          ) : (
            <ul className="space-y-2">
              {departures.slice(0, 6).map(d => (
                <li key={d.company} className="flex items-center justify-between gap-2">
                  <button onClick={onGoToMovements} className="min-w-0 text-left group">
                    <span className="text-sm font-medium text-gray-800 group-hover:text-violet-700 group-hover:underline truncate block" title={d.company}>{d.company}</span>
                    <span className="text-xs text-gray-500 truncate block">
                      {d.people.slice(0, 2).map(p => p.name).join(', ')}{d.people.length > 2 ? '…' : ''}
                    </span>
                  </button>
                  <span className="text-xs font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded-full whitespace-nowrap">−{d.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonitoringOverview;
